import React, { useEffect } from 'react';
import { CheckCircle2, ArrowRight, Github } from 'lucide-react';

export default function ProlineSkillPage() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const capabilities = [
        'Creates leads in ProLine the moment they come in — no manual entry',
        'Books inspections with the right rep, moves the stage, logs it',
        'Reads inspection notes and drafts context-aware follow-up emails — staged in ProLine ready to send',
        'Runs a pipeline audit on command: stale deals, missing follow-ups, stuck stages',
        'Checks weather against every scheduled job nightly — flags rain risk before 6am',
        'Scores deal quality (A/B/C/F) based on note completeness and process compliance',
        'Syncs your full pipeline to a local database — reports run in seconds, not 20 minutes',
    ];

    return (
        <div className="min-h-screen bg-[#0D0D0F] text-[#F0EFF4]">

            {/* Hero */}
            <div className="max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
                <p className="font-mono text-xs tracking-[0.2em] text-[#7B61FF] uppercase mb-6">
                    Free RoofClaw Skill
                </p>
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-white leading-tight mb-6">
                    ProLine CRM — Now Operated by Your Agent
                </h1>
                <p className="text-xl text-[#F0EFF4]/70 leading-relaxed max-w-2xl mx-auto mb-10">
                    The proline-claw skill connects your RoofClaw to ProLine's Partner API. Lead creation, appointment booking, pipeline audits, draft emails, rain day scheduling — without your reps touching the CRM.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                        href="https://www.roofingbusinesspartner.com/meetings/adamsandlinks/roofclaw-consultation"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-semibold px-8 py-4 rounded-xl transition-colors duration-200"
                    >
                        Add ProLine CRM Skills to my RoofClaw
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </div>

            {/* What It Does */}
            <div className="max-w-3xl mx-auto px-6 pb-16">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-8 mb-10">
                    <p className="font-mono text-xs tracking-[0.2em] text-[#7B61FF] uppercase mb-6">
                        What It Does
                    </p>
                    <ul className="space-y-4">
                        {capabilities.map((cap, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <CheckCircle2 className="w-5 h-5 text-[#7B61FF] flex-shrink-0 mt-0.5" />
                                <span className="text-[#F0EFF4]/80 leading-relaxed">{cap}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Divider */}
                <div className="my-12 border-t border-[#F0EFF4]/10" />

                {/* GitHub Section */}
                <div className="mb-12">
                    <div className="flex items-center gap-3 mb-4">
                        <Github className="w-5 h-5 text-[#7B61FF]" />
                        <p className="font-mono text-xs tracking-[0.2em] text-[#7B61FF] uppercase">
                            Open Source. MIT Licensed. Built by RoofClaw.
                        </p>
                    </div>
                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8">
                        <p className="text-[#F0EFF4]/70 mb-4 leading-relaxed">
                            The proline-claw skill is fully open source. Every script, every API call, every documented gotcha — visible and auditable.
                        </p>
                        <a
                            href="https://github.com/Roofing-Business-Partner/proline-claw"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#7B61FF] hover:text-[#9B81FF] font-semibold transition-colors duration-200 mb-6 group"
                        >
                            <Github className="w-4 h-4" />
                            github.com/Roofing-Business-Partner/proline-claw
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                        </a>
                        <p className="text-[#F0EFF4]/40 text-sm leading-relaxed border-t border-[#F0EFF4]/5 pt-4 mt-2">
                            The skill is free. What RoofClaw adds is the identity layer, the memory, the other 12 skills, and someone to call when it breaks.
                        </p>
                    </div>
                </div>

                {/* Divider */}
                <div className="my-12 border-t border-[#F0EFF4]/10" />

                {/* X Post Card */}
                <div className="mb-12">
                    <p className="font-mono text-xs tracking-[0.2em] text-[#7B61FF] uppercase mb-6">
                        From the Feed
                    </p>
                    <div className="bg-[#15202B] border border-[#F0EFF4]/10 rounded-2xl p-6 shadow-lg">
                        {/* Tweet Header */}
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-[#7B61FF]/20 border border-[#7B61FF]/30 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-[#7B61FF]">A</span>
                            </div>
                            <div>
                                <p className="font-semibold text-white text-sm">Adam Sand</p>
                                <p className="text-[#8899A6] text-xs">@RoofingPartner</p>
                            </div>
                            <div className="ml-auto">
                                <svg className="w-5 h-5 text-[#1D9BF0]" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.766l7.33-8.442L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                            </div>
                        </div>
                        {/* Tweet Content */}
                        <div className="text-[#F0EFF4]/90 text-sm leading-relaxed space-y-3">
                            <p>We built a ProLine CRM skill for OpenClaw agents.</p>
                            <p>7 working API endpoints. Local SQLite mirror. Weather-aware job scheduling. Deal quality scoring. Context-aware email drafting from inspection notes.</p>
                            <p>Open source. MIT licensed.</p>
                            <p>If your RoofClaw runs ProLine — it's already yours.</p>
                            <p>
                                <a
                                    href="https://github.com/Roofing-Business-Partner/proline-claw"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#1D9BF0] hover:underline"
                                >
                                    github.com/Roofing-Business-Partner/proline-claw
                                </a>
                            </p>
                        </div>
                        {/* Tweet Footer */}
                        <div className="mt-4 pt-3 border-t border-[#F0EFF4]/5 flex gap-4 text-xs text-[#8899A6]">
                            <span>April 21, 2026</span>
                            <span>·</span>
                            <span>X (Twitter)</span>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="my-12 border-t border-[#F0EFF4]/10" />

                {/* Primary CTA */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                    <a
                        href="https://www.roofingbusinesspartner.com/meetings/adamsandlinks/roofclaw-consultation"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-semibold px-8 py-4 rounded-xl transition-colors duration-200"
                    >
                        Add ProLine CRM Skills to my RoofClaw
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>

                {/* Secondary CTA */}
                <div className="text-center">
                    <p className="text-[#F0EFF4]/40 text-sm">
                        Don't have RoofClaw yet?{' '}
                        <a href="/" className="text-[#7B61FF] hover:text-[#9B81FF] transition-colors duration-200">
                            See what it is →
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
